import React, { useState, useEffect, useContext } from 'react';
import { API, AuthContext } from '@/App';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, CheckCircle2 } from 'lucide-react';
import PdfDownloadButton from '@/components/PdfDownloadButton';

// 5-Week Pre-Stocktake Checklist Data
const weeklyChecklists = [
  {
    week: 1,
    title: 'Week 1',
    items: [
      { id: 'w1-1', department: 'Admin', title: 'Work through DPOs Weekly', description: '' },
      { id: 'w1-2', department: 'GI', title: 'Clean Up Key Area - GI Desk', description: 'Remove all stock from the area - reduce store use items' },
      { id: 'w1-3', department: 'Builders', title: 'Timber Yard Price Audit', description: "Ensure the tickets match - don't just rescan the existing - thorough check required" },
      { id: 'w1-4', department: 'Lifestyles', title: 'Review Non Live status lines for the department', description: 'Lines that are set to Non Live that have positive SOH - can we sell these?' },
      { id: 'w1-5', department: 'ITH', title: 'Clean Up Key Service Area - Paint Desk', description: 'Remove all stock from the area - reduce store use items' },
      { id: 'w1-6', department: 'Support', title: 'Service Desk - Review SOH of Smart Home Bays Keys - Miscuts etc', description: 'Where stock is incorrect and unable to sell - write off. Miscuts should have a zero SOH' },
    ]
  },
  {
    week: 2,
    title: 'Week 2',
    items: [
      { id: 'w2-1', department: 'Admin', title: 'Handstock Team Review', description: '' },
      { id: 'w2-2', department: 'GI', title: 'Review Claims / Isolations', description: 'Resolve stock that is no longer required to be there. Do not reverse claims - review when the claim was raised & follow up with the supplier for collection' },
      { id: 'w2-3', department: 'Builders', title: 'Review small loose handstock in toolshop', description: 'Bundle where possible i.e. drill bits. Bundle by Item Number - think how do you make it easier to count' },
      { id: 'w2-4', department: 'Lifestyles', title: 'Deleted/Quit/Archived Lines in Lifestyles', description: 'Review lines from the Microstrategy Report - Yesterdays SOH' },
      { id: 'w2-5', department: 'ITH', title: 'Dexion Dive Key Areas - Signs, Seeds, Numerals, Irrigation', description: 'Tidy behind Bays' },
      { id: 'w2-6', department: 'Support', title: 'Review Microstrategy Report - COS/Kitchens CSO Lines', description: 'Review Positive SOH lines - Refer to How to sheet. Review all Click & Collect Orders - Return to shelf items that are not being collected / cancelled orders. Ensure Stock is unpicked in the system when completing this' },
    ]
  },
  {
    week: 3,
    title: 'Week 3',
    items: [
      { id: 'w3-1', department: 'Admin', title: 'Handstock Team Review', description: '' },
      { id: 'w3-2', department: 'GI', title: 'Negative Timber - Deep Dive & Review', description: 'Do not just write on all your negative timber! Corresponding write ons and offs need to occur - engage your Inventory Expert for support' },
      { id: 'w3-3', department: 'Builders', title: 'Deleted/Quit/Archived Lines in Builders - Sell through', description: 'Review Chlorine Claims / Finelines with IE - lines in detail. Look for any outstanding claims where stock has already left - review incorrect usage of finelines etc' },
      { id: 'w3-4', department: 'Lifestyles', title: 'Bay Maintenance - Irrigation', description: 'Tidy Bays - review SOH of key lines & Gaps' },
      { id: 'w3-5', department: 'ITH', title: 'Tidy Bays and review SOH of key lines / gaps', description: '' },
      { id: 'w3-6', department: 'Support', title: 'Clean Up Key Service Areas', description: 'Special Orders Desk & Tool Shop Register. Remove all stock from the areas - reduce store use items' },
    ]
  },
  {
    week: 4,
    title: 'Week 4',
    items: [
      { id: 'w4-1', department: 'Admin', title: 'Handstock Team Review', description: '' },
      { id: 'w4-2', department: 'GI', title: 'Top Stock Reset - Full review', description: 'Ensure all Future builds have the Top Stock One pager attached. Ensure all builds are accurate - limit amount of lines built to one pallet - continue to review moving forwards' },
      { id: 'w4-3', department: 'Builders', title: 'Deleted/Quit/Archived Lines in Inside the Home - Sell through', description: '' },
      { id: 'w4-4', department: 'Lifestyles', title: 'Bay Maintenance - Fixings', description: 'Tidy Bays - review SOH of key lines' },
      { id: 'w4-5', department: 'ITH', title: 'Clean up all corners of shop', description: 'Dexion Dive Key Areas - Paint, Flooring, Plumbing, Electrical. Look at all the corners of the store where stock etc can land - Review store use items' },
      { id: 'w4-6', department: 'Support', title: 'Clean Up Key Service Area - Service Desk', description: 'Remove all stock from the area - reduce store use items' },
    ]
  },
  {
    week: 5,
    title: 'Week 5',
    items: [
      { id: 'w5-1', department: 'Admin', title: 'Review Last Years top 10 Shrink & Swells by Leadership Group', description: 'Review Lines to ensure no kick back from last ST. Where required adjust the stock - if ongoing issues identify root cause and resolve' },
      { id: 'w5-2', department: 'GI', title: 'All Kitchens/Trade Stock has IN attached', description: 'For all lines that do not have a scannable barcode - ensure order number is attached where applicable and item numbers & lengths are clearly labelled' },
      { id: 'w5-3', department: 'Builders', title: 'Dexion Dive Key Areas - Hardware', description: 'Tidy behind Bays' },
      { id: 'w5-4', department: 'Lifestyles', title: 'Count & Review LPG Gas', description: 'Use Gas Cycle Count Sheet to review gas SOH and identify root cause - maintain this throughout and work through process breakdowns' },
      { id: 'w5-5', department: 'ITH', title: 'Review & Resolve Hard to Count Bays - Plumbing', description: 'Tidy Bays - review SOH of key lines & Gaps' },
      { id: 'w5-6', department: 'Support', title: 'Clean Up Key Service Area - Click and Collect Bays & Desk Area', description: 'Remove all unnecessary stock from the area - reduce store use items or excess items that do not belong in the space' },
    ]
  }
];

const departmentColors = {
  'Admin': 'bg-purple-100 text-purple-800 border-purple-200',
  'GI': 'bg-blue-100 text-blue-800 border-blue-200',
  'Builders': 'bg-orange-100 text-orange-800 border-orange-200',
  'Lifestyles': 'bg-green-100 text-green-800 border-green-200',
  'ITH': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Support': 'bg-pink-100 text-pink-800 border-pink-200',
};

const PreStocktake = () => {
  const { user } = useContext(AuthContext);
  const [expandedWeeks, setExpandedWeeks] = useState({ 1: true });
  const [completedItems, setCompletedItems] = useState({});
  const token = localStorage.getItem('token');

  // Get the current store_id (for Network Admin, check localStorage)
  const getStoreId = () => {
    if (user?.role === 'network_admin' || user?.role === 'regional_manager') {
      return localStorage.getItem('selectedStoreId') || user?.store_id;
    }
    return user?.store_id;
  };

  useEffect(() => {
    loadSavedProgress();
  }, [user?.store_id]);

  const loadSavedProgress = async () => {
    try {
      const storeId = getStoreId();
      const url = storeId ? `${API}/checklist-progress?store_id=${storeId}` : `${API}/checklist-progress`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCompletedItems(data.completed_items || {});
      }
    } catch (error) {
      // Load from localStorage as fallback
      const saved = localStorage.getItem('prestocktake_progress');
      if (saved) {
        setCompletedItems(JSON.parse(saved));
      }
    }
  };

  const saveProgress = async (newCompleted) => {
    // Save to localStorage immediately
    localStorage.setItem('prestocktake_progress', JSON.stringify(newCompleted));
    
    // Try to save to backend with store_id
    try {
      const storeId = getStoreId();
      await fetch(`${API}/checklist-progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          completed_items: newCompleted,
          store_id: storeId
        })
      });
    } catch (error) {
      console.error('Failed to save to backend', error);
    }
  };

  const toggleWeek = (week) => {
    setExpandedWeeks(prev => ({
      ...prev,
      [week]: !prev[week]
    }));
  };

  const toggleItem = (itemId) => {
    const newCompleted = {
      ...completedItems,
      [itemId]: completedItems[itemId] ? null : new Date().toISOString()
    };
    if (!newCompleted[itemId]) {
      delete newCompleted[itemId];
    }
    setCompletedItems(newCompleted);
    saveProgress(newCompleted);
    toast.success(newCompleted[itemId] ? 'Task completed!' : 'Task unmarked');
  };

  const getWeekProgress = (week) => {
    const items = week.items;
    const completed = items.filter(item => completedItems[item.id]).length;
    const total = items.length;
    const percentage = Math.round((completed / total) * 100);
    return { completed, total, percentage };
  };

  const getOverallProgress = () => {
    const allItems = weeklyChecklists.flatMap(w => w.items);
    const completed = allItems.filter(item => completedItems[item.id]).length;
    const total = allItems.length;
    const percentage = Math.round((completed / total) * 100);
    return { completed, total, percentage };
  };

  const overall = getOverallProgress();

  return (
    <div className="space-y-6" data-testid="pre-stocktake-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight mb-2">Pre-Stocktake</h1>
          <p className="text-slate-600">5-week preparation checklist before count day</p>
        </div>
        <PdfDownloadButton 
          contentId="pre-stocktake-content" 
          filename="Pre-Stocktake_Checklist" 
          title="Pre-Stocktake Checklist"
        />
      </div>

      <div id="pre-stocktake-content">
        {/* Overall Progress Card */}
        <Card className="p-6 bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-teal-900">Overall Progress</h3>
            <p className="text-sm text-teal-700">{overall.completed} of {overall.total} tasks completed</p>
          </div>
          <div className="text-3xl font-bold text-teal-600">{overall.percentage}%</div>
        </div>
        <div className="w-full bg-teal-200 rounded-full h-3 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-teal-500 to-teal-600 transition-all duration-500"
            style={{ width: `${overall.percentage}%` }}
          />
        </div>
      </Card>

      {/* Weekly Accordion Checklists */}
      <div className="space-y-3">
        {weeklyChecklists.map((week) => {
          const progress = getWeekProgress(week);
          const isExpanded = expandedWeeks[week.week];
          
          return (
            <Card key={week.week} className="overflow-hidden" data-testid={`week-${week.week}-accordion`}>
              {/* Accordion Header */}
              <button
                onClick={() => toggleWeek(week.week)}
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                data-testid={`week-${week.week}-toggle`}
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-slate-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-500" />
                  )}
                  <span className="text-lg font-semibold text-slate-900">{week.title}</span>
                  {progress.percentage === 100 && (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-32 bg-slate-200 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${
                        progress.percentage === 100 ? 'bg-green-500' :
                        progress.percentage >= 50 ? 'bg-orange-500' : 'bg-blue-500'
                      }`}
                      style={{ width: `${progress.percentage}%` }}
                    />
                  </div>
                  <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                    progress.percentage === 100 ? 'bg-green-100 text-green-700' :
                    progress.percentage >= 50 ? 'bg-orange-100 text-orange-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {progress.completed}/{progress.total}
                  </span>
                </div>
              </button>

              {/* Accordion Content */}
              {isExpanded && (
                <div className="border-t border-slate-200 bg-slate-50/50">
                  <div className="divide-y divide-slate-200">
                    {week.items.map((item) => {
                      const isCompleted = !!completedItems[item.id];
                      return (
                        <div
                          key={item.id}
                          className="flex items-start gap-4 p-4 hover:bg-white transition-colors"
                          data-testid={`checklist-item-${item.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={isCompleted}
                            onChange={() => toggleItem(item.id)}
                            className="mt-1 w-5 h-5 text-teal-600 rounded border-slate-300 focus:ring-teal-500 cursor-pointer"
                            data-testid={`checkbox-${item.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded border ${departmentColors[item.department]}`}>
                                {item.department}
                              </span>
                            </div>
                            <div className={`font-medium ${isCompleted ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                              {item.title}
                            </div>
                            {item.description && (
                              <p className={`text-sm mt-1 ${isCompleted ? 'text-slate-400' : 'text-slate-600'}`}>
                                {item.description}
                              </p>
                            )}
                            {isCompleted && completedItems[item.id] && (
                              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" />
                                Completed {new Date(completedItems[item.id]).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
      </div>
    </div>
  );
};

export default PreStocktake;
